import { Injectable } from '@angular/core';
import { CrmObjectInterface } from '@vendasta/crm';
import { BehaviorSubject } from 'rxjs';
import { ObjectType } from '../tokens-and-interfaces';
import { SystemFieldIds } from '../shared-services/crm-services/field.service';

@Injectable()
export class PreviewObjectService {
  private previewCrmObject$$: BehaviorSubject<CrmObjectInterface | null> =
    new BehaviorSubject<CrmObjectInterface | null>(null);

  private createDatesForPreview(previewObject: CrmObjectInterface, objectType: ObjectType): void {
    let objectCreatedFieldId: string;
    let objectUpdatedFieldId: string;
    switch (objectType) {
      case 'Contact':
        objectCreatedFieldId = SystemFieldIds.ContactCreated;
        objectUpdatedFieldId = SystemFieldIds.ContactUpdated;
        break;
      case 'Company':
        objectCreatedFieldId = SystemFieldIds.CompanyCreated;
        objectUpdatedFieldId = SystemFieldIds.CompanyUpdated;
        break;
    }

    // guarantee preview object will show proper dates for updated/created columns
    const createdDateIndex = previewObject.fields?.findIndex((f) => f.fieldId === objectCreatedFieldId) || -1;
    const updatedDateIndex = previewObject.fields?.findIndex((f) => f.fieldId === objectUpdatedFieldId) || -1;
    if (
      createdDateIndex !== -1 &&
      !!previewObject?.fields &&
      previewObject?.fields[createdDateIndex]?.dateValue === null
    ) {
      previewObject.fields[createdDateIndex].dateValue = new Date();
    } else if (createdDateIndex === -1 && !!previewObject?.fields) {
      previewObject.fields.push({
        fieldId: SystemFieldIds.ContactCreated,
        externalId: SystemFieldIds.ContactCreated,
        dateValue: new Date(),
      });
    }
    if (
      updatedDateIndex !== -1 &&
      !!previewObject?.fields &&
      previewObject?.fields[updatedDateIndex]?.dateValue === null
    ) {
      previewObject.fields[updatedDateIndex].dateValue = new Date();
    } else if (updatedDateIndex === -1 && !!previewObject?.fields) {
      previewObject.fields.push({
        fieldId: SystemFieldIds.ContactUpdated,
        externalId: SystemFieldIds.ContactUpdated,
        dateValue: new Date(),
      });
    }
  }

  setPreviewCrmObject(previewObject: CrmObjectInterface | null, objectType: ObjectType): void {
    if (previewObject === null) {
      this.previewCrmObject$$.next(null);
      return;
    }
    this.createDatesForPreview(previewObject, objectType);

    // do a deep copy of the object, only adding the ID if necessary
    const deepCopyObject: CrmObjectInterface = JSON.parse(JSON.stringify(previewObject));
    this.previewCrmObject$$.next(deepCopyObject);
  }

  clearPreview(): void {
    this.previewCrmObject$$.next(null);
  }

  withPreview(crmObjects: CrmObjectInterface[]): CrmObjectInterface[] {
    const crmObjectsCopy = [...crmObjects];
    this.applyCrmObjectPreview(crmObjectsCopy);
    return crmObjectsCopy;
  }

  private applyCrmObjectPreview(crmObjects: CrmObjectInterface[]): void {
    const previewCrmObject = this.previewCrmObject$$.getValue();
    if (previewCrmObject === null) {
      return;
    }

    // don't display the preview and (stale / already returned) value at the same time
    const existsAlreadyAtIndex = crmObjects.findIndex((obj) => obj.crmObjectId === previewCrmObject.crmObjectId);
    if (existsAlreadyAtIndex !== -1) {
      crmObjects.splice(existsAlreadyAtIndex, 1);
    }

    // show the preview at the top of the table
    crmObjects.unshift(previewCrmObject);
  }
}
